import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail, openProtectedFile } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Pagination, usePagination } from "@/components/Pagination";
import { BuyAgainButton } from "@/components/BuyAgain";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { payWithRazorpay } from "@/lib/razorpayCheckout";
import { Package, Repeat, Pause, Play, SkipForward, X, Edit3, Loader2, Truck, PhoneCall, ExternalLink, AlertTriangle, Star } from "lucide-react";

const PAGE_SIZE = 20;

const STATUS_STYLES = {
  received: "bg-stone-100 text-stone-700",
  preparing: "bg-marigold/15 text-marigold",
  accepted: "bg-marigold/15 text-marigold",
  ready_for_pickup: "bg-marigold/15 text-marigold",
  picked_up: "bg-teal/15 text-teal",
  out_for_delivery: "bg-teal/15 text-teal",
  delivered: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-destructive/10 text-destructive",
};

export default function CustomerDashboard({ embedded = false, initialTab = null }) {
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [tab, setTab] = useState(() => initialTab || (new URLSearchParams(window.location.search).get("tab") === "subs" ? "subs" : "orders"));
  const [loadingIds, setLoadingIds] = useState({});
  const [ordersPage, setOrdersPage] = useState(1);
  const [subsPage, setSubsPage] = useState(1);

  const load = async () => {
    const [o, s] = await Promise.all([
      api.get("/customer/orders"),
      api.get("/customer/subscriptions"),
    ]);
    setOrders([...(o.data || [])].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));   // latest first
    setSubs(s.data);
  };

  useEffect(() => { load(); }, []);

  // act(sub, action) or act(sub, "update", {quantity, frequency, next_delivery_date, address_id})
  const act = async (sub, action, extra = {}) => {
    setLoadingIds(l => ({ ...l, [sub.id]: true }));
    try {
      const { data } = await api.patch(`/customer/subscriptions/${sub.id}`, { action, ...extra });
      const done = { update: "updated", modify: "updated", accept_price: "restarted at the new price", skip: "— next delivery skipped" }[action] || `${action}d`;
      toast.success(data?.note || `Subscription ${done}`);
      await load();
      return true;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      return false;
    } finally {
      setLoadingIds(l => ({ ...l, [sub.id]: false }));
    }
  };

  return (
    <div className={embedded ? "" : "max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8"}>
      {!embedded && (
        <div className="mb-4">
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">My orders</h1>
        </div>
      )}

      {/* E-C12: a small strip so the orders list starts above the fold on phones. */}
      <div className="flex flex-wrap gap-2 mb-5 text-sm" data-testid="customer-stats">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-3 py-1"><Package className="h-4 w-4 text-marigold" /><b>{orders.length}</b> orders</span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-3 py-1"><Package className="h-4 w-4 text-emerald-600" /><b>{orders.filter(o => o.status === "delivered").length}</b> delivered</span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-3 py-1"><Repeat className="h-4 w-4 text-teal" /><b>{subs.filter(s => s.status === "active").length}</b> active subscriptions</span>
      </div>

      <UnfinishedPayments onCompleted={load} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className={`rounded-full bg-stone-100 p-1 h-12 ${embedded ? "hidden" : ""}`}>
          <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-full px-6">Orders ({orders.length})</TabsTrigger>
          <TabsTrigger value="subs" data-testid="tab-subs" className="rounded-full px-6">Subscriptions ({subs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-6">
          <OrdersList orders={orders} page={ordersPage} setPage={setOrdersPage} onChanged={load} />
          <ShopPhoneOrders />
        </TabsContent>

        <TabsContent value="subs" className="mt-6">
          <SubsList subs={subs} act={act} reload={load} loadingIds={loadingIds} page={subsPage} setPage={setSubsPage} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/** What you bought at a shop outside the app: home deliveries a shop took
 *  for you and bills from the shop counter, on your account by your mobile
 *  number. Shown only when there are some; read-only. */
function ShopPhoneOrders() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/customer/shop-orders").then(({ data }) => setRows(data || []), () => {}); }, []);
  if (!rows.length) return null;
  const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
  return (
    <div className="mt-8" data-testid="shop-phone-orders">
      <h2 className="font-heading text-xl font-semibold">Bought at shops</h2>
      <p className="text-sm text-stone-600 mb-3">Bills from a shop's counter and home deliveries a shop took for you by phone or WhatsApp. For changes, call the shop.</p>
      <div className="grid gap-3 md:grid-cols-2">
        {rows.map((o) => (
          <div key={o.id} className="rounded-2xl border border-stone-200 bg-white p-4 text-sm">
            <div className="flex justify-between gap-3">
              <div className="font-semibold">{o.shop_name} · {o.number}</div>
              <span className={`rounded-full px-2 py-0.5 text-xs ${o.status === "delivered" ? "bg-emerald-100 text-emerald-800" : "bg-marigold/20 text-stoneWarm"}`}>{o.status_label}</span>
            </div>
            <div className="text-stone-600 mt-1">{(o.lines || []).map((l) => `${l.qty} × ${l.name}${l.size ? ` (${l.size})` : ""}`).join(", ")}</div>
            <div className="mt-1 text-stone-500">{rupees(o.total)} · {o.kind === "counter" ? "bought at the shop" : "home delivery"} · {new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div>
            {o.bill_number && (
              <button type="button" data-testid={`shop-bill-${o.id}`} className="mt-2 text-xs font-semibold text-teal underline"
                onClick={() => openProtectedFile(`/customer/shop-orders/${o.kind}/${o.id}/bill.pdf`).catch(() => {})}>Bill {o.bill_number}</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// G8: payments that went through but never became an order (browser closed,
// network drop). The customer can finish the order or see the refund status.
function UnfinishedPayments({ onCompleted }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/customer/payments/unfinished").then(({ data }) => setRows(data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  if (rows.length === 0) return null;

  const complete = async (p) => {
    setBusy(p.razorpay_order_id);
    try {
      await api.post(`/customer/payments/${p.razorpay_order_id}/complete`);
      toast.success("Your order has been placed");
      await load();
      await onCompleted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      await load();
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-2 mb-6" data-testid="unfinished-payments">
      {rows.map(p => (
        <Card key={p.razorpay_order_id} className="p-4 rounded-2xl border-marigold/40 bg-marigold/5 flex flex-wrap items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-marigold" />
          <div className="flex-1 min-w-0 text-sm">
            <div className="font-semibold">We received your payment of ₹{Number(p.amount || 0).toFixed(0)} on {new Date(p.created_at).toLocaleString()}, but the order wasn't placed.</div>
            <div className="text-stone-600">
              {p.refund_status === "refunded" ? "It has been refunded — it can take 5–7 business days to show in your account."
                : p.refund_status === "refund_initiated" ? "A refund is being processed."
                : p.refund_status === "refund_failed" ? "Our automatic refund failed; our team will refund you manually."
                : p.can_complete ? "You can finish placing it now, or it will be refunded automatically within a few hours."
                : "It will be refunded automatically."}
            </div>
          </div>
          {p.can_complete && (
            <Button size="sm" disabled={!!busy} onClick={() => complete(p)} className="rounded-full bg-stoneWarm text-white hover:bg-stoneWarm/90" data-testid={`complete-payment-${p.razorpay_order_id}`}>
              {busy === p.razorpay_order_id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Place my order"}
            </Button>
          )}
        </Card>
      ))}
    </div>
  );
}

function OrdersList({ orders, page, setPage, onChanged }) {
  const { pageCount, page: p, paged } = usePagination(orders, PAGE_SIZE, page, setPage);
  if (orders.length === 0) {
    return (
      <Card className="p-10 text-center border-dashed rounded-3xl">
        <p className="text-stone-600">No orders yet.</p>
      </Card>
    );
  }
  return (
    <>
      <div className="space-y-3">
        {paged.map(o => (
          <Card key={o.id} data-testid={`customer-order-${o.id}`} className="p-4 rounded-2xl border-stone-200 hover:shadow-md hover:border-stone-300 transition-all cursor-pointer" onClick={(e) => { if (e.target.closest("a,button,summary,input,label")) return; const el = document.getElementById(`order-details-${o.id}`); if (el) el.open = !el.open; }}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs text-stone-500">Order #{o.id.slice(0,8).toUpperCase()} · {new Date(o.created_at).toLocaleString()}</div>
                  <div className="font-heading font-semibold text-lg mt-1">{o.vendor_name}</div>
                  <div className="text-sm text-stone-600 mt-1">{(o.items || []).map(i => `${i.quantity} × ${i.name || "Item"}`).join(", ")}</div>
                  {o.is_subscription && <Badge className="mt-2 bg-teal/15 text-teal border-0 rounded-full text-[10px]">Subscription order</Badge>}
                </div>
                <div className="text-right">
                  <Badge className={`${STATUS_STYLES[o.status]} border-0 rounded-full capitalize`}>{o.status.replace(/_/g,' ')}</Badge>
                  <div className="font-heading text-xl font-bold mt-2">₹{o.total.toFixed(0)}</div>
                </div>
              </div>
            <details id={`order-details-${o.id}`} className="mt-3 group">
              <summary className="text-xs text-marigold font-semibold cursor-pointer select-none list-none inline-flex items-center gap-1" data-testid={`customer-order-toggle-${o.id}`}>
                <span className="group-open:hidden">Show details</span>
                <span className="hidden group-open:inline">Hide details</span>
              </summary>
              <div className="pt-3 border-t border-stone-100 mt-2 text-sm text-stone-700 space-y-1">
                <div><b>Delivery to:</b> {o.delivery_address || "—"} · {o.delivery_pincode}</div>
                {(o.items || []).map((i, idx) => (
                  <div key={i.product_id ? `${i.product_id}-${idx}` : `${i.name || 'item'}-${idx}`} className="flex justify-between">
                    <span>{i.quantity} × {i.name || "Item"}{i.unit_value && i.unit ? ` (${i.unit_value} ${i.unit})` : ""}</span>
                    <span className="tabular-nums">₹{((i.line_gross ?? (i.price * i.quantity)) || 0).toFixed(0)}</span>
                  </div>
                ))}
              </div>
            </details>
            <CustomerDeliveryStrip order={o} />
            <OrderActions order={o} onChanged={onChanged} />
            {["delivered", "cancelled"].includes(o.status) && !o.source && !o.donation && (
              <div className="mt-3"><BuyAgainButton orderId={o.id} /></div>
            )}
            {o.status === "delivered" && !o.source && (
              <FeedbackRow orderId={o.id} vendorName={o.vendor_name} />
            )}
          </Card>
        ))}
      </div>
      <Pagination page={p} pageCount={pageCount} onChange={setPage} total={orders.length} pageSize={PAGE_SIZE} testidPrefix="cust-orders-pg" />
    </>
  );
}

// Iter-8: 3-D feedback (product quality / vendor experience / delivery experience)
function FeedbackRow({ orderId, vendorName }) {
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/customer/orders/${orderId}/feedback`)
      .then(({ data }) => { if (alive) setExisting(data && data.id ? data : null); })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [orderId]);

  if (loading) return null;
  if (existing) {
    return (
      <div className="mt-3 pt-3 border-t border-stone-100 flex flex-wrap gap-4 items-center text-xs text-stone-600" data-testid={`customer-feedback-summary-${orderId}`}>
        <span className="font-medium text-stone-700">Your rating:</span>
        <RatingChip label="Product" value={existing.product_quality} />
        <RatingChip label="Vendor" value={existing.vendor_experience} />
        <RatingChip label="Delivery" value={existing.delivery_experience} />
        {existing.comment && <span className="italic text-stone-500 truncate max-w-xs">"{existing.comment}"</span>}
      </div>
    );
  }
  return (
    <div className="mt-3 pt-3 border-t border-stone-100 flex justify-end">
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid={`customer-rate-order-btn-${orderId}`}
        className="rounded-full text-xs border-marigold text-marigold hover:bg-marigold hover:text-stoneWarm"
      >
        <Star className="h-3.5 w-3.5 mr-1" /> Rate this order
      </Button>
      <FeedbackDialog
        open={open}
        setOpen={setOpen}
        orderId={orderId}
        vendorName={vendorName}
        onSaved={(fb) => setExisting(fb)}
      />
    </div>
  );
}

function RatingChip({ label, value }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span>{label}:</span>
      <span className="font-semibold text-marigold">{value}/5</span>
    </span>
  );
}

function StarPicker({ value, onChange, testidPrefix }) {
  return (
    <div className="flex gap-1">
      {[1,2,3,4,5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          data-testid={`${testidPrefix}-${n}`}
          className={`transition-transform hover:scale-110 ${n <= value ? "text-marigold" : "text-stone-300"}`}
          aria-label={`${n} star${n>1?"s":""}`}
        >
          <Star className="h-6 w-6 fill-current" />
        </button>
      ))}
    </div>
  );
}

function FeedbackDialog({ open, setOpen, orderId, vendorName, onSaved }) {
  const [pq, setPq] = useState(0);
  const [ve, setVe] = useState(0);
  const [de, setDe] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (pq < 1 || ve < 1 || de < 1) { toast.error("Please rate all three dimensions"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/customer/orders/${orderId}/feedback`, {
        product_quality: pq, vendor_experience: ve, delivery_experience: de, comment: comment.trim() || undefined,
      });
      toast.success("Thanks for your feedback!");
      onSaved(data);
      setOpen(false);
    } catch (e) { toast.error(formatApiErrorDetail(e, "Could not submit feedback")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid={`customer-feedback-dialog-${orderId}`} className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Rate your order</DialogTitle>
          <p className="text-sm text-stone-500 mt-1">Help other pet parents by rating {vendorName} on three things:</p>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <FeedbackRow_ label="Product quality" hint="Was the product fresh, undamaged, and as described?" value={pq} onChange={setPq} testidPrefix="fb-pq" />
          <FeedbackRow_ label="Vendor experience" hint="Communication, packaging, professionalism." value={ve} onChange={setVe} testidPrefix="fb-ve" />
          <FeedbackRow_ label="Delivery experience" hint="On time? Handled with care?" value={de} onChange={setDe} testidPrefix="fb-de" />
          <div className="space-y-2">
            <div className="text-sm font-medium">Anything else? <span className="text-stone-400 font-normal">(optional)</span></div>
            <Textarea rows={3} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="fb-comment" placeholder="A few kind words go a long way" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="fb-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="fb-submit" className="bg-marigold text-stoneWarm hover:bg-marigold/90">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FeedbackRow_({ label, hint, value, onChange, testidPrefix }) {
  return (
    <div>
      <div className="flex justify-between items-baseline mb-1">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-stone-500">{value ? `${value}/5` : "Not rated"}</div>
      </div>
      <p className="text-xs text-stone-500 mb-2">{hint}</p>
      <StarPicker value={value} onChange={onChange} testidPrefix={testidPrefix} />
    </div>
  );
}

function SubsList({ subs, act, reload, loadingIds, page, setPage }) {
  const { pageCount, page: p, paged } = usePagination(subs, PAGE_SIZE, page, setPage);
  if (subs.length === 0) {
    return (
      <Card className="p-10 text-center border-dashed rounded-3xl">
        <p className="text-stone-600">No subscriptions yet. Browse products and pick "Subscribe and Save".</p>
      </Card>
    );
  }
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {paged.map(s => (
          <SubCard key={s.id} sub={s} act={act} reload={reload} loading={!!loadingIds[s.id]} />
        ))}
      </div>
      <Pagination page={p} pageCount={pageCount} onChange={setPage} total={subs.length} pageSize={PAGE_SIZE} testidPrefix="cust-subs-pg" />
    </>
  );
}


const FREQ_DAYS = { weekly: 7, biweekly: 14, monthly: 30 };
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fmtDay = (iso) => new Date(iso).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
const METHOD = { upi: "UPI", card: "card" };

function SubCard({ sub, act, reload, loading }) {
  const { user } = useAuth();
  const [paying, setPaying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const isActive = sub.status === "active";
  const isPaused = sub.status === "paused";
  const isCancelled = sub.status === "cancelled";
  const hold = sub.price_hold;
  const change = sub.price_change;
  const charge = sub.charge;
  const autopayOn = sub.autopay?.status === "active";
  const due = charge && ["failed", "needs_payment"].includes(charge.status);
  const price = Number(sub.accepted_price || sub.product_price || 0);

  // CUS-1: Pay now for a waiting delivery, or turn auto-pay on (₹1 check, refunded).
  const pay = async (kind) => {
    setPaying(true);
    try {
      const url = kind === "autopay" ? `/customer/subscriptions/${sub.id}/autopay` : `/customer/subscriptions/${sub.id}/pay-now`;
      const { data } = await api.post(url, { autopay: true, method: sub.autopay?.method || "upi" });
      if (!data.paid) {
        const ctx = await payWithRazorpay(data, { user, description: kind === "autopay" ? "Turn on auto-pay (₹1, refunded)" : `${sub.product_name} delivery` });
        await api.post(`/customer/subscriptions/${sub.id}/payment-confirm`, ctx);
      }
      toast.success(kind === "autopay" ? "Auto-pay is on" : "Paid — your delivery is on its way");
      await reload();
    } catch (err) {
      toast.error(err?.message === "Payment cancelled" ? "Payment cancelled" : (formatApiErrorDetail(err.response?.data?.detail) || err.message));
    } finally { setPaying(false); }
  };
  const openEdit = () => {
    const nd = new Date(sub.next_delivery_date);
    setF({ quantity: sub.quantity, frequency: sub.frequency, date: ymd(nd), address_id: sub.delivery_address_id || "" });
    setEditing(true);
    api.get("/customer/addresses").then(({ data }) => setAddresses((data || []).filter(a => !a.needs_location))).catch(() => {});
  };
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  const latest = new Date(); latest.setDate(latest.getDate() + (FREQ_DAYS[f?.frequency || sub.frequency] || 7));

  const save = async () => {
    const extra = {};
    if (f.quantity !== sub.quantity) extra.quantity = f.quantity;
    if (f.frequency !== sub.frequency) extra.frequency = f.frequency;
    if (f.date !== ymd(new Date(sub.next_delivery_date))) extra.next_delivery_date = f.date;
    if (f.address_id && f.address_id !== sub.delivery_address_id) extra.address_id = f.address_id;
    if (!Object.keys(extra).length) { setEditing(false); return; }
    if (await act(sub, "update", extra)) setEditing(false);
  };

  return (
    <Card data-testid={`sub-card-${sub.id}`} className="p-5 rounded-2xl border-stone-200">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs text-stone-500 uppercase tracking-widest">{sub.vendor_name}</div>
          <div className="font-heading font-semibold text-lg mt-1 leading-tight">{sub.product_name}</div>
          <div className="text-sm text-stone-600 mt-1">{sub.quantity} × ₹{price.toFixed(0)} = <b>₹{(sub.quantity * price).toFixed(0)}</b> · <span className="capitalize">{sub.frequency}</span></div>
          {!isCancelled && (
            <div className="text-xs text-stone-500 mt-2">Next delivery: <b>{due ? "as soon as it's paid" : fmtDay(charge?.cycle_for || sub.next_delivery_date)}</b> · {autopayOn ? `auto-pay (${METHOD[sub.autopay.method] || "UPI"})` : "auto-pay needed"}</div>
          )}
          {sub.delivery_address && <div className="text-xs text-stone-500 mt-1 truncate">To: {sub.delivery_address}</div>}
        </div>
        <Badge className={`border-0 rounded-full capitalize ${isActive ? "bg-emerald-100 text-emerald-700" : isPaused ? "bg-marigold/15 text-marigold" : "bg-destructive/10 text-destructive"}`}>{sub.status}</Badge>
      </div>

      {due && !isCancelled && (
        <div className="mt-3 rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm" data-testid={`sub-pay-due-${sub.id}`}>
          <div className="font-semibold text-stoneWarm">₹{Number(charge.amount).toFixed(0)} to pay for your delivery{Number(charge.amount) > Number(charge.price) * Number(charge.quantity) + 0.5 ? " (incl. delivery fee)" : ""}</div>
          <div className="text-xs text-stone-600 mt-0.5">
            {charge.status === "failed" ? `Auto-pay didn't go through (${charge.last_error}). ` : ""}
            {isPaused ? "Your subscription is paused until it's paid." : charge.status === "failed" ? "We'll try again tomorrow, or pay now so it goes out sooner." : "It goes out as soon as it's paid."}
          </div>
          {!autopayOn && <div className="text-xs text-stone-600 mt-1">Paying now also turns on auto-pay for the next deliveries.</div>}
          <Button size="sm" onClick={() => pay("charge")} disabled={paying} data-testid={`sub-pay-now-${sub.id}`} className="mt-2 rounded-full bg-stoneWarm text-white hover:bg-stoneWarm/90">
            {paying ? <Loader2 className="h-3 w-3 animate-spin" /> : `Pay now ₹${Number(charge.amount).toFixed(0)}`}
          </Button>
        </div>
      )}

      {charge?.status === "charging" && !isCancelled && (
        <div className="mt-3 rounded-xl border border-stone-200 bg-stone-50 p-3 text-xs text-stone-600" data-testid={`sub-charging-${sub.id}`}>
          Collecting ₹{Number(charge.amount).toFixed(0)} by auto-pay for your delivery on {fmtDay(charge.cycle_for)}. Your bank or UPI app notifies you before the money is taken.
        </div>
      )}

      {charge?.status === "paid" && !isCancelled && (
        <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800" data-testid={`sub-paid-${sub.id}`}>
          Paid ₹{Number(charge.amount).toFixed(0)} for your delivery on {fmtDay(charge.cycle_for)} — it goes to the shop that day. To change it, wait until after it arrives (or cancel for a full refund).
        </div>
      )}

      {change && !hold && !isCancelled && (
        <div className="mt-3 rounded-xl border border-marigold/40 bg-marigold/10 p-3 text-sm" data-testid={`sub-price-change-${sub.id}`}>
          <div className="font-semibold text-stoneWarm">Price going up: ₹{Number(change.old_price).toFixed(0)} → ₹{Number(change.new_price).toFixed(0)} from {fmtDay(change.effective_from)}</div>
          <div className="text-xs text-stone-600 mt-0.5">Deliveries before then stay at ₹{Number(change.old_price).toFixed(0)}. Don't want it? Change or cancel before {fmtDay(change.effective_from)}.</div>
        </div>
      )}

      {!isCancelled && !due && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs" data-testid={`sub-autopay-${sub.id}`}>
          {autopayOn ? (
            <>
              <Badge className="border-0 rounded-full bg-emerald-100 text-emerald-700">Auto-pay on · {METHOD[sub.autopay.method] || "UPI"}{sub.autopay.max_amount ? ` · up to ₹${Number(sub.autopay.max_amount).toFixed(0)}` : ""}</Badge>
            </>
          ) : (
            <>
              <Badge className="border-0 rounded-full bg-marigold/15 text-stoneWarm">Auto-pay needed</Badge>
              <Button size="sm" variant="outline" onClick={() => pay("autopay")} disabled={paying} data-testid={`sub-autopay-on-${sub.id}`} className="rounded-full h-7">
                Turn on auto-pay
              </Button>
              <span className="text-stone-500">A ₹1 check, refunded straight away.</span>
            </>
          )}
        </div>
      )}

      {hold && !isCancelled && (
        <div className="mt-3 rounded-xl border border-marigold/40 bg-marigold/10 p-3 text-sm" data-testid={`sub-price-hold-${sub.id}`}>
          <div className="font-semibold text-stoneWarm">Price went up: ₹{Number(hold.old_price).toFixed(0)} → ₹{Number(hold.new_price).toFixed(0)}</div>
          <div className="text-xs text-stone-600 mt-0.5">We've paused deliveries until you agree. Accept to continue, or cancel the subscription.</div>
          <Button size="sm" onClick={() => act(sub, "accept_price")} disabled={loading} data-testid={`sub-accept-price-${sub.id}`} className="mt-2 rounded-full bg-stoneWarm text-white hover:bg-stoneWarm/90">
            Accept ₹{Number(hold.new_price).toFixed(0)} and continue
          </Button>
        </div>
      )}

      {!isCancelled && (
        <div className="mt-4 flex flex-wrap gap-2">
          {isActive ? (
            <Button size="sm" variant="outline" onClick={() => act(sub, "pause")} disabled={loading} data-testid={`sub-pause-${sub.id}`} className="rounded-full">
              <Pause className="h-3 w-3 mr-1" /> Pause
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => act(sub, "resume")} disabled={loading} data-testid={`sub-resume-${sub.id}`} className="rounded-full">
              <Play className="h-3 w-3 mr-1" /> Resume
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => act(sub, "skip")} disabled={loading} data-testid={`sub-skip-${sub.id}`} className="rounded-full">
            <SkipForward className="h-3 w-3 mr-1" /> Skip next
          </Button>
          <Button size="sm" variant="outline" onClick={() => (editing ? setEditing(false) : openEdit())} disabled={loading} data-testid={`sub-modify-${sub.id}`} className="rounded-full">
            <Edit3 className="h-3 w-3 mr-1" /> Change
          </Button>
          <Button size="sm" variant="outline" onClick={() => window.confirm("Cancel this subscription?") && act(sub, "cancel")} disabled={loading} data-testid={`sub-cancel-${sub.id}`} className="rounded-full text-destructive hover:text-destructive">
            <X className="h-3 w-3 mr-1" /> Cancel
          </Button>
        </div>
      )}

      {editing && f && !isCancelled && (
        <div className="mt-4 pt-4 border-t border-stone-200 grid grid-cols-2 gap-3" data-testid={`sub-edit-${sub.id}`}>
          <label className="text-xs text-stone-600">Next delivery
            <input type="date" value={f.date} min={ymd(tomorrow)} max={ymd(latest)} onChange={e => setF(p => ({ ...p, date: e.target.value }))}
              className="mt-1 h-10 w-full rounded-xl border border-stone-300 px-2 text-sm" data-testid={`sub-date-${sub.id}`} />
          </label>
          <label className="text-xs text-stone-600">Quantity per delivery
            <Input type="number" min={1} max={100} value={f.quantity} onChange={e => setF(p => ({ ...p, quantity: Math.max(1, Math.min(100, +e.target.value || 1)) }))} className="mt-1 h-10" data-testid={`sub-qty-input-${sub.id}`} />
          </label>
          <label className="text-xs text-stone-600">How often
            <select value={f.frequency} onChange={e => setF(p => ({ ...p, frequency: e.target.value }))} className="mt-1 h-10 w-full rounded-xl border border-stone-300 px-2 text-sm bg-white" data-testid={`sub-freq-${sub.id}`}>
              <option value="weekly">Weekly</option><option value="biweekly">Every 2 weeks</option><option value="monthly">Monthly</option>
            </select>
          </label>
          <label className="text-xs text-stone-600">Deliver to
            <select value={f.address_id} onChange={e => setF(p => ({ ...p, address_id: e.target.value }))} className="mt-1 h-10 w-full rounded-xl border border-stone-300 px-2 text-sm bg-white" data-testid={`sub-address-${sub.id}`}>
              {!f.address_id && <option value="">Current address</option>}
              {addresses.map(a => <option key={a.id} value={a.id}>{a.label} — {[a.flat_no, a.address].filter(Boolean).join(", ").slice(0, 40)}</option>)}
            </select>
          </label>
          <p className="col-span-2 text-[11px] text-stone-500">If your shop doesn't deliver to the new address, we'll move the subscription to a nearby shop that does. If it costs more you'll be told first, with notice before you pay more.</p>
          <div className="col-span-2 flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Close</Button>
            <Button size="sm" onClick={save} disabled={loading} data-testid={`sub-save-qty-${sub.id}`} className="rounded-full bg-stoneWarm text-white">
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save changes"}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}


const REFUND_LABELS = {
  refund_required: ["Refund pending", "bg-marigold/15 text-marigold"],
  refund_initiated: ["Refund in progress", "bg-marigold/15 text-marigold"],
  refunded: ["Refunded", "bg-teal/15 text-teal"],
  refund_failed: ["Refund delayed — our team is on it", "bg-destructive/10 text-destructive"],
};

// G10–G16: what the customer can do with an order from its card.
function OrderActions({ order: o, onChanged }) {
  const [otp, setOtp] = useState(null);
  const [busy, setBusy] = useState("");
  const inTransit = (["picked_up", "out_for_delivery"].includes(o.delivery_status) || ["picked_up", "out_for_delivery"].includes(o.status)) && o.status !== "delivered";
  const failed = o.delivery_status === "failed" && !["delivered", "cancelled"].includes(o.status);
  const refund = REFUND_LABELS[o.refund_status];

  useEffect(() => {
    if (!inTransit) { setOtp(null); return; }
    let live = true;
    api.get(`/customer/orders/${o.id}/delivery-otp`).then(({ data }) => { if (live) setOtp(data); }).catch(() => {});
    return () => { live = false; };
  }, [o.id, inTransit]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); toast.success(ok); await onChanged(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(""); }
  };
  const cancel = () => {
    if (!window.confirm("Cancel this order? If you've paid, the money goes back to your original payment method.")) return;
    run("cancel", () => api.post(`/customer/orders/${o.id}/cancel`, { reason: "changed_mind" }), "Order cancelled");
  };
  const failedChoice = (action) => run(action, () => api.post(`/customer/orders/${o.id}/failed-delivery`, { action }),
    action === "redeliver" ? "We've asked the shop to deliver again" : "Order cancelled — your refund is on its way");

  return (
    <div className="mt-3 space-y-2">
      {otp?.otp_active && otp.code && (
        <div className="flex items-center gap-3 text-sm bg-teal/5 border border-teal/20 rounded-xl px-4 py-2.5" data-testid={`cust-otp-${o.id}`}>
          <span>Delivery OTP</span>
          <span className="font-mono text-lg font-bold tracking-[0.3em]">{otp.code}</span>
          <span className="text-xs text-stone-500">Share only with your delivery agent at handover.</span>
        </div>
      )}
      {failed && (
        <div className="flex flex-wrap items-center gap-3 text-sm bg-destructive/5 border border-destructive/20 rounded-xl px-4 py-2.5" data-testid={`cust-failed-${o.id}`}>
          <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
          <span className="flex-1">We couldn't deliver this order. What would you like to do?</span>
          <Button size="sm" variant="outline" disabled={!!busy} onClick={() => failedChoice("redeliver")} className="rounded-full h-8">Deliver again</Button>
          <Button size="sm" variant="outline" disabled={!!busy} onClick={() => failedChoice("refund")} className="rounded-full h-8">Cancel and refund</Button>
        </div>
      )}
      {o.status === "received" && o.scheduled_for && !o.released_at && (
        <div className="text-xs text-marigold font-semibold" data-testid={`cust-scheduled-${o.id}`}>
          {o.delivery_promise || "Scheduled"} — the shop was closed when you ordered, so it gets your order when it opens.
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        {refund && <Badge className={`${refund[1]} border-0 rounded-full`} data-testid={`cust-refund-${o.id}`}>{refund[0]}{o.refunded_amount ? ` · ₹${Number(o.refunded_amount).toFixed(0)}` : ""}</Badge>}
        {!["delivered", "cancelled"].includes(o.status) && !o.source && (
          <Link to={`/orders/${o.id}/track`} className="font-semibold text-teal hover:underline" data-testid={`cust-track-page-${o.id}`}>Track order</Link>
        )}
        {/* The tax invoices are made at delivery; they're on the order's page. */}
        {o.status === "delivered" && !o.source && (
          <Link to={`/orders/${o.id}/track`} className="font-semibold text-teal hover:underline" data-testid={`cust-invoices-${o.id}`}>Invoices</Link>
        )}
        {o.status === "received" && (
          <button type="button" disabled={!!busy} onClick={cancel} className="font-semibold text-destructive hover:underline" data-testid={`cust-cancel-${o.id}`}>
            {busy === "cancel" ? "Cancelling…" : "Cancel order"}
          </button>
        )}
        <Link to={`/help?order=${o.id}`} className="text-stone-500 hover:underline" data-testid={`cust-help-${o.id}`}>Get help</Link>
      </div>
    </div>
  );
}

function CustomerDeliveryStrip({ order: o }) {
  const [calling, setCalling] = useState(false);

  const call = async () => {
    setCalling(true);
    try {
      const { data } = await api.post(`/orders/${o.id}/call-agent`);
      if (data.phone) window.location.href = `tel:${data.phone.replace(/[^\d+]/g, "")}`;
      toast.success(data.message || "Calling the shop…");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setCalling(false); }
  };

  if (o.delivery_method === "partner") {
    const eta = o.delivery_partner_eta_min;
    const etaStr = eta == null ? "—" : eta < 60 ? `${eta} min` : eta < 1440 ? `${Math.round(eta/60)} h` : `${Math.round(eta/1440)} day${eta>=2880?"s":""}`;
    return (
      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm bg-teal/5 border border-teal/20 rounded-xl px-4 py-2.5">
        <Truck className="h-4 w-4 text-teal shrink-0" />
        <div className="flex-1">
          Delivered by <b>{o.delivery_partner_name}</b> · ETA <b>{etaStr}</b>
        </div>
        {o.tracking_url && (
          <a href={o.tracking_url} target="_blank" rel="noreferrer" data-testid={`cust-track-${o.id}`} className="text-xs font-semibold text-teal hover:underline flex items-center gap-1">
            Track <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    );
  }
  if (o.delivery_method === "vendor" || o.delivery_method === "self") {
    const personName = o.vendor_delivery_person_name || o.self_delivery_person_name;
    const personPhoneMasked = o.vendor_delivery_person_phone_masked || o.self_delivery_person_phone_masked;
    return (
      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm bg-marigold/10 border border-marigold/30 rounded-xl px-4 py-2.5">
        <Truck className="h-4 w-4 text-marigold shrink-0" />
        <div className="flex-1">
          Delivered by shop staff · <b>{personName}</b> · <span className="font-mono text-xs">{personPhoneMasked}</span>
        </div>
        <Button size="sm" onClick={call} disabled={calling} data-testid={`cust-call-${o.id}`} className="rounded-full h-8 bg-marigold text-stoneWarm hover:bg-marigold/90 font-semibold">
          {calling ? <Loader2 className="h-3 w-3 animate-spin" /> : <><PhoneCall className="h-3 w-3 mr-1" /> Call shop</>}
        </Button>
      </div>
    );
  }
  if (o.delivery_method === "pending_selection" || o.delivery_method === "pending_assignment") {
    return (
      <div className="mt-3 flex items-center gap-3 text-sm bg-stone-100 border border-stone-200 rounded-xl px-4 py-2.5 text-stone-700">
        <Loader2 className="h-4 w-4 text-stone-500 animate-spin shrink-0" />
        Arranging delivery — vendor is confirming.
      </div>
    );
  }
  if (o.delivery_method === "failed") {
    return (
      <div className="mt-3 flex items-center gap-3 text-sm bg-destructive/5 border border-destructive/20 rounded-xl px-4 py-2.5 text-destructive">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        Delivery couldn't be arranged — the vendor will contact you.
      </div>
    );
  }
  return null;
}
